import { useMemo } from 'react';
import Card from '../ui/Card';
import { UptimeCheck, groupChecksByDay } from '../../lib/uptime';

interface UptimeCalendarProps {
  checks: UptimeCheck[];
  days?: number;
}

const NO_DATA_COLOR = 'var(--surface-active)';

function getDotColor(availability: number | null): string {
  if (availability === null) return NO_DATA_COLOR;
  if (availability >= 99.9) return 'var(--status-up)';
  if (availability >= 99) return 'var(--status-warn)';
  return 'var(--status-down)';
}

export default function UptimeCalendar({ checks, days = 90 }: UptimeCalendarProps) {
  const calendarData = useMemo(() => {
    const grouped = groupChecksByDay(checks);
    const today = new Date();
    const result: Array<{
      date: string;
      displayDate: string;
      availability: number | null;
    }> = [];

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const dayData = grouped.get(dateStr);

      result.push({
        date: dateStr,
        displayDate: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        availability: dayData?.availability ?? null,
      });
    }

    return result;
  }, [checks, days]);

  const getTooltip = (item: (typeof calendarData)[0]) => {
    if (item.availability === null) return `${item.displayDate}: No data`;
    return `${item.displayDate}: ${item.availability}% uptime`;
  };

  const legend: Array<{ color: string; label: string }> = [
    { color: 'var(--status-up)', label: '100%' },
    { color: 'var(--status-warn)', label: '99%+' },
    { color: 'var(--status-down)', label: '<99%' },
    { color: NO_DATA_COLOR, label: 'No data' },
  ];

  return (
    <Card
      eyebrow="History"
      title={`${days}-Day Uptime`}
      actions={
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          {legend.map(item => (
            <span
              key={item.label}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}
            >
              <span
                style={{
                  width: '7px',
                  height: '7px',
                  borderRadius: '2px',
                  background: item.color,
                }}
              />
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                {item.label}
              </span>
            </span>
          ))}
        </div>
      }
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
        {calendarData.map(day => (
          <span
            key={day.date}
            title={getTooltip(day)}
            style={{
              width: '10px',
              height: '10px',
              borderRadius: '2px',
              background: getDotColor(day.availability),
              cursor: 'default',
              transition: 'transform var(--dur-fast) var(--ease-out)',
            }}
            onMouseEnter={e => {
              (e.target as HTMLElement).style.transform = 'scale(1.5)';
            }}
            onMouseLeave={e => {
              (e.target as HTMLElement).style.transform = 'scale(1)';
            }}
          />
        ))}
      </div>

      <div
        className="t-num"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 'var(--space-3)',
          fontSize: 'var(--text-xs)',
          color: 'var(--text-faint)',
        }}
      >
        <span>{days} days ago</span>
        <span>Today</span>
      </div>
    </Card>
  );
}
